import {tokens, breakpoints, headerSizes} from '@commercial-helios/ui/theme'
import {m as motion, AnimatePresence} from 'framer-motion'
import {graphql, useStaticQuery} from 'gatsby'
import type {ReactElement} from 'react'
import React, {useState} from 'react'
import {useInView} from 'react-intersection-observer'

import {SignupModal} from '@/components/signup-modal'
import type {HeaderQuery} from '@/graphql-types'
import useHasMounted from '@/hooks/use-has-mounted'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {useUser} from '@/hooks/use-user'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import ExternalLink from '@helios/shared/components/external-link'
import Image from '@helios/shared/components/image'
import LocalizedLink from '@helios/shared/components/localized-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import RichText from '@helios/shared/components/rich-text'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import logoAngled from '@helios/shared/images/icons/logo-angled.svg'
import logoHorizontal from '@helios/shared/images/icons/logo-horizontal.svg'
import menuCloseIcon from '@helios/shared/images/icons/menu-close.svg'
import menuOpenIcon from '@helios/shared/images/icons/menu-open.svg'
import type {UrlCode} from '@helios/shared/locale/locales'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useLocale} from '@helios/shared/providers/locale'
import {Text} from '@helios/shared/styles/typography'
import type ImageSource from '@helios/shared/types/image'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import {durationStringToNumber} from '@helios/shared/utils/duration-string-to-number'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'

import * as s from './header.styles'

export interface Props {
    hideLeftNav?: boolean
    hideRightNav?: boolean
    ctaDestination?: 'signupform' | 'demopage' | 'partnersform'
}

type Content = PrismicData<HeaderQuery, 'allPrismicHeader'>['data']
type ContentDropdowns = Content['dropdowns']

interface NavDropdownProps {
    id: string
    title?: Content['product_dropdown_title']
    body?: Content['product_dropdown_body']
    links?: Content['product_dropdown_links']
    content?:
        | ReactElement<Content['product_dropdown_content']>
        | ReactElement<Content['why_pleo_dropdown_content_team']>
        | ReactElement<Content['why_pleo_dropdown_content_industry']>
        | ReactElement<Content['why_pleo_dropdown_content_company_size']>
        | ReactElement<Content['resources_dropdown_content']>
    betaLabel?: string
}

interface WhyPleoDropdownContentColumnProps {
    linkItems:
        | Content['why_pleo_dropdown_content_industry']
        | Content['why_pleo_dropdown_content_team']
        | Content['why_pleo_dropdown_content_company_size']
    categoryTitle: string
    categoryShortSentence: string
}

type ProductDropdownContentAreaProps = {
    content: Content['product_dropdown_content']
    betaLabel: string
}

const ProductDropdownContentArea = ({content, betaLabel}: ProductDropdownContentAreaProps) => {
    if (!content) {
        return null
    }

    return (
        <s.DropdownContentArea columns={2}>
            {content.map((item) => {
                const iconBgColor = isValidBackgroundColor(item.icon_background_color)
                    ? item.icon_background_color
                    : 'default'

                return (
                    <s.DropdownContentAreaItem key={item.title} as={PrismicLink} {...item.link}>
                        {!!item.icon && (
                            <s.IconWrapper color={iconBgColor}>
                                <Image image={item.icon} width={80} />
                            </s.IconWrapper>
                        )}

                        <div>
                            <Text variant="paragraph-large">
                                {item.title} {item.is_beta && <s.BetaTag>{betaLabel}</s.BetaTag>}
                            </Text>

                            <s.DropdownItemBodyTextWrapper>
                                <RichText
                                    variant="label-base"
                                    color="muted"
                                    content={item.body.richText}
                                />
                            </s.DropdownItemBodyTextWrapper>
                        </div>
                    </s.DropdownContentAreaItem>
                )
            })}
        </s.DropdownContentArea>
    )
}

const WhyPleoDropdownContentColumn = ({
    linkItems,
    categoryTitle,
    categoryShortSentence,
}: WhyPleoDropdownContentColumnProps) => {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <s.WhyPleoDropdownContentColumn $isExpanded={isOpen}>
            <s.WhyPleoDropdownHeadingWrapper>
                <s.NestedDropdownCategoryTitle onClick={() => setIsOpen(!isOpen)}>
                    {categoryTitle}
                    <s.Arrow $isExpanded={isOpen} aria-hidden="true" />
                </s.NestedDropdownCategoryTitle>

                <s.WhyPleoDropdownCategorySentence>
                    {categoryShortSentence}
                </s.WhyPleoDropdownCategorySentence>
            </s.WhyPleoDropdownHeadingWrapper>

            <s.NestedDropdown $isExpanded={isOpen} $direction="column">
                {linkItems?.map((item, i) => {
                    return (
                        <s.DropdownContentAreaItem
                            key={`${item.title}-${i}`}
                            as={PrismicLink}
                            {...item.link}
                        >
                            {!!item.icon && <Image image={item.icon} width={40} />}

                            <Text variant="paragraph-large">{item.title}</Text>
                        </s.DropdownContentAreaItem>
                    )
                })}
            </s.NestedDropdown>
        </s.WhyPleoDropdownContentColumn>
    )
}

type ResourcesDropdownContentAreaProps = {
    content: Content['resources_dropdown_content']
}

const ResourcesDropdownContentArea = ({content}: ResourcesDropdownContentAreaProps) => {
    if (!content) {
        return null
    }

    return (
        <s.DropdownContentArea columns={3}>
            {content.map((item, i) => {
                return (
                    <s.DropdownContentAreaItem
                        key={`${item.title}-${i}`}
                        as={PrismicLink}
                        {...item.link}
                    >
                        {!!item.icon && <Image image={item.icon} width={40} />}

                        <div>
                            <Text variant="paragraph-large">{item.title}</Text>

                            <s.DropdownItemBodyTextWrapper>
                                <RichText
                                    variant="label-base"
                                    color="muted"
                                    content={item.body.richText}
                                />
                            </s.DropdownItemBodyTextWrapper>
                        </div>
                    </s.DropdownContentAreaItem>
                )
            })}
        </s.DropdownContentArea>
    )
}

const NavDropdown = ({title, body, links, content}: NavDropdownProps) => {
    return (
        <s.DropdownWrapper>
            <s.Dropdown>
                <s.DropdownInner>
                    <s.DropdownTitleArea>
                        <s.DropdownTitleAreaHeader>
                            <RichText variant="heading-base" content={title?.richText} />
                            <RichText
                                variant="paragraph-base"
                                color="muted"
                                content={body?.richText}
                            />
                        </s.DropdownTitleAreaHeader>

                        {links && hasNonEmptyItems(links) && (
                            <s.DropdownTitleAreaLinks>
                                {links.map((linkItem, i) => {
                                    return (
                                        <s.DropdownTitleAreaLink
                                            key={`${linkItem.link_title}-${i}`}
                                            as={PrismicLink}
                                            {...linkItem.link}
                                        >
                                            {linkItem.link_title}{' '}
                                            <s.LinkArrow aria-hidden="true">→</s.LinkArrow>
                                        </s.DropdownTitleAreaLink>
                                    )
                                })}
                            </s.DropdownTitleAreaLinks>
                        )}
                    </s.DropdownTitleArea>
                    {content}
                </s.DropdownInner>
            </s.Dropdown>
        </s.DropdownWrapper>
    )
}

interface NestedDropdownColumnProps {
    title: string
    icon: ImageSource
    linkItems: {
        title: string
        link: PrismicLinkProps
    }[]
    hasOneColumn: boolean
}

const NestedDropdownColumn = ({
    title,
    icon,
    linkItems,
    hasOneColumn,
}: NestedDropdownColumnProps) => {
    const [isOpen, setIsOpen] = useState(false || hasOneColumn)

    return (
        <s.NestedDropdownColumn $isExpanded={isOpen}>
            <s.ColumnHeader $isOnlyColumn={hasOneColumn}>
                <Image image={icon} />
                <s.NestedDropdownCategoryTitle onClick={() => setIsOpen(!isOpen)}>
                    <s.ColumnHeaderTitle>{title}</s.ColumnHeaderTitle>
                    <s.Arrow $isExpanded={isOpen} aria-hidden="true" />
                </s.NestedDropdownCategoryTitle>
            </s.ColumnHeader>

            <s.NestedDropdown $hasOneColumn={hasOneColumn} $isExpanded={isOpen}>
                {linkItems?.map((item, i) => {
                    return (
                        <s.NavLinkWithHover
                            key={`${item.title}-${i}`}
                            as={PrismicLink}
                            {...item.link}
                        >
                            {item.title}
                        </s.NavLinkWithHover>
                    )
                })}
            </s.NestedDropdown>
        </s.NestedDropdownColumn>
    )
}

type UniformDropdownProps = ContentDropdowns[number]['dropdown']['document']['data'] & {
    showAd: boolean
}

const UniformDropdown = ({columns, cta_column, showAd}: UniformDropdownProps) => {
    const hasOneColumn = columns.length === 1

    return (
        <s.DropdownWrapper>
            <s.DropdownSingle>
                <s.DropdownColumnsWrapper>
                    {columns.slice(0, 3).map((column) => {
                        return (
                            <div key={column.column.document.id}>
                                <NestedDropdownColumn
                                    title={column.column.document.data.label}
                                    icon={column.column.document.data.icon}
                                    linkItems={column.column.document.data.links.map((item) => ({
                                        title: item.link_text,
                                        link: item.link,
                                    }))}
                                    hasOneColumn={hasOneColumn}
                                />
                            </div>
                        )
                    })}
                    {showAd && (
                        <s.DropdownAd
                            $large={hasOneColumn}
                            $bgColor={
                                s.isAllowedBgColor(cta_column.document.data.background_color)
                                    ? cta_column.document.data.background_color
                                    : 'pink'
                            }
                        >
                            <Image image={cta_column.document.data.image} />
                            <s.DropdownAdBodyWrapper>
                                <Text variant="paragraph-large">
                                    {cta_column.document.data.body}
                                </Text>
                                <s.NavCta as={PrismicLink} {...cta_column.document.data.cta_link}>
                                    {cta_column.document.data.cta_text}
                                </s.NavCta>
                            </s.DropdownAdBodyWrapper>
                        </s.DropdownAd>
                    )}
                </s.DropdownColumnsWrapper>
            </s.DropdownSingle>
        </s.DropdownWrapper>
    )
}

interface HeaderProps extends Props {
    content: Content
}

const transition = {
    type: 'tween',
    duration: durationStringToNumber(tokens.smooth) / 1000,
}

const animationOverlayProps = {
    initial: {opacity: 0},
    animate: {opacity: 1},
    exit: {opacity: 0},
    transition,
}

const animationDropdownProps = {
    initial: {
        opacity: 0,
        height: 0,
    },
    animate: {
        opacity: 1,
        height: 'auto',
        transition,
    },
    exit: {
        opacity: 0,
        height: 0,
        transition: {
            ...transition,
            delay: 0.1,
        },
    },
}

const localesWithRevampedNav: UrlCode[] = ['en', 'da', 'de', 'es', 'se']

export const Header = ({
    content,
    hideLeftNav,
    hideRightNav,
    ctaDestination = 'signupform',
}: HeaderProps) => {
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedDropdownIndex, setSelectedDropdownIndex] = useState<number | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const isXSmallScreen = useMediaQuery.max(s.mobileMenuBreakpoint)
    const isSmallScreen = useMediaQuery.max(s.tabletMenuBreakpoint)
    const [mobileCtaRef, inView] = useInView({initialInView: true, skip: !isXSmallScreen})
    const showStickyCta = !inView && !menuOpen
    const [headerScrollRef, headerInView] = useInView({initialInView: true, skip: isSmallScreen})
    const showExploreProduct = menuOpen || !headerInView
    const showLogIn = menuOpen || headerInView
    const {home, menu} = useGlobalStrings()

    const hasMounted = useHasMounted()
    const {isLoggedIn} = useUser()
    const currentLocale = useLocale()

    const toggleMenu = () => {
        setSelectedDropdownIndex(null)
        setMenuOpen((isOpen) => !isOpen)
    }

    const handleTouchOrClick = (dropdownIndex: number) => {
        setSelectedDropdownIndex(selectedDropdownIndex !== dropdownIndex ? dropdownIndex : null)
    }

    const handleHover = (dropdownIndex: number | null) => {
        if (window.innerWidth >= breakpoints[s.laptopMenuBreakpoint]) {
            setSelectedDropdownIndex(dropdownIndex)
        }
    }

    const onSignupCtaClick = () => {
        setMenuOpen(false)
        setSelectedDropdownIndex(null)
        setModalOpen(true)

        track(TrackingEvent.HeaderGetStartedActioned, {action: 'started'})
    }

    const sectionsWithDropdown = [
        {
            id: 'product',
            label: content.product_button_label,
            title: content.product_dropdown_title,
            body: content.product_dropdown_body,
            links: content.product_dropdown_links,
            isBeta: content.is_beta_label,
            content: (
                <ProductDropdownContentArea
                    content={content.product_dropdown_content}
                    betaLabel={content.is_beta_label}
                />
            ),
        },
        {
            id: 'why-pleo',
            label: content.why_pleo_button_label,
            title: content.why_pleo_dropdown_title,
            body: content.why_pleo_dropdown_body,
            links: content.why_pleo_dropdown_links,
            content: (
                <s.DropdownContentArea columns={2}>
                    {content.why_pleo_dropdown_company_size_label &&
                    content.why_pleo_dropdown_content_company_size.length ? (
                        <WhyPleoDropdownContentColumn
                            categoryTitle={content.why_pleo_dropdown_company_size_label}
                            categoryShortSentence={
                                content.why_pleo_dropdown_company_size_short_sentence
                            }
                            linkItems={content.why_pleo_dropdown_content_company_size}
                        />
                    ) : (
                        <WhyPleoDropdownContentColumn
                            categoryTitle={content.why_pleo_dropdown_team_label}
                            categoryShortSentence={content.why_pleo_dropdown_team_short_sentence}
                            linkItems={content.why_pleo_dropdown_content_team}
                        />
                    )}
                    <WhyPleoDropdownContentColumn
                        categoryTitle={content.why_pleo_dropdown_industry_label}
                        categoryShortSentence={content.why_pleo_dropdown_industry_short_sentence}
                        linkItems={content.why_pleo_dropdown_content_industry}
                    />
                </s.DropdownContentArea>
            ),
        },
        {
            id: 'resources',
            label: content.resources_button_label,
            title: content.resources_dropdown_title,
            body: content.resources_dropdown_body,
            links: content.resources_dropdown_links,
            content: <ResourcesDropdownContentArea content={content.resources_dropdown_content} />,
        },
        {
            id: 'partners',
            label: content.partners_button_label,
            directLink: content?.partners_button_link,
        },
    ]

    const sectionsWithDropdownV2 = [
        {
            content: content.dropdowns[0]?.dropdown.document?.data,
        },
        {
            content: content.dropdowns[1]?.dropdown.document?.data,
        },
        {
            content: content.dropdowns[2]?.dropdown.document?.data,
        },
        {
            content: content.dropdowns[3]?.dropdown.document?.data,
        },
    ]

    const highlightedFeatures = [
        content.signup_modal[0].highlighted_feature_1,
        content.signup_modal[0].highlighted_feature_2,
        content.signup_modal[0].highlighted_feature_3,
    ].filter((item) => Boolean(item))

    const getCtaButton = (ctaDestination: Props['ctaDestination']) => {
        switch (ctaDestination) {
            case 'signupform': {
                return <s.NavCta onClick={onSignupCtaClick}>{content.start_free_trial}</s.NavCta>
            }

            case 'demopage': {
                return (
                    <s.NavCta as={LocalizedLink} to="/demo">
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            case 'partnersform': {
                return (
                    <s.NavCta
                        as={LocalizedLink}
                        to="/bigform-partner"
                        onClick={() =>
                            track(TrackingEvent.HeaderGetStartedActioned, {action: 'partners'})
                        }
                    >
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            default: {
                throw new Error('Unrecognized destination')
            }
        }
    }

    return (
        <>
            <AnimatePresence>
                {(selectedDropdownIndex !== null || menuOpen) && (
                    <s.Overlay
                        {...animationOverlayProps}
                        mobileMenuOpen={menuOpen}
                        dropdownOpen={selectedDropdownIndex !== null}
                    />
                )}
            </AnimatePresence>

            <s.ScrollTrigger ref={headerScrollRef} $threshold={`${headerSizes.heightLarge}px`} />

            <s.HeaderWrapper $menuOpen={menuOpen} enabled={menuOpen} forwardedAs="header">
                <s.HeaderInner $menuOpen={menuOpen}>
                    <s.DesktopLogo>
                        <LocalizedLink to="/">
                            <img src={logoAngled} alt={home} />
                        </LocalizedLink>
                    </s.DesktopLogo>
                    {showStickyCta ? (
                        <s.StickyCtaWrapper>{getCtaButton(ctaDestination)}</s.StickyCtaWrapper>
                    ) : (
                        <s.MobileLogo>
                            <LocalizedLink to="/">
                                <img src={logoHorizontal} alt={home} />
                            </LocalizedLink>
                        </s.MobileLogo>
                    )}

                    {(!hideLeftNav || !hideRightNav) && (
                        <>
                            <s.Nav>
                                {!hideLeftNav && (
                                    <s.NavGroupLeft>
                                        {sectionsWithDropdown.map((section, i) => {
                                            const isActive = selectedDropdownIndex === i
                                            const shouldRenderUniformDropdown =
                                                localesWithRevampedNav.includes(
                                                    currentLocale.urlCode
                                                ) && sectionsWithDropdownV2[i].content

                                            if (shouldRenderUniformDropdown || section.content) {
                                                return (
                                                    <s.NavMenu
                                                        onMouseEnter={() => handleHover(i)}
                                                        onMouseLeave={() => handleHover(null)}
                                                        key={section.id}
                                                    >
                                                        <s.MenuBtnWithDropdown
                                                            aria-expanded={isActive}
                                                            aria-controls={section.id}
                                                            onClick={() => handleTouchOrClick(i)}
                                                        >
                                                            <s.NavLink
                                                                as="span"
                                                                $isHighlighted={isActive}
                                                            >
                                                                {section.label}
                                                                <s.Arrow
                                                                    $isExpanded={isActive}
                                                                    aria-hidden="true"
                                                                />
                                                            </s.NavLink>
                                                        </s.MenuBtnWithDropdown>

                                                        <div id={section.id} tabIndex={-1}>
                                                            <AnimatePresence initial={false}>
                                                                {isActive && (
                                                                    <motion.div
                                                                        {...animationDropdownProps}
                                                                        key={`${section.id}-${i}`}
                                                                    >
                                                                        {shouldRenderUniformDropdown ? (
                                                                            <UniformDropdown
                                                                                showAd={
                                                                                    sectionsWithDropdownV2[
                                                                                        i
                                                                                    ].content
                                                                                        .columns
                                                                                        .length <
                                                                                        3 &&
                                                                                    !isSmallScreen
                                                                                }
                                                                                {...sectionsWithDropdownV2[
                                                                                    i
                                                                                ].content}
                                                                            />
                                                                        ) : (
                                                                            <NavDropdown
                                                                                {...section}
                                                                            />
                                                                        )}
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </div>
                                                    </s.NavMenu>
                                                )
                                            }

                                            if (section.directLink) {
                                                return (
                                                    <s.NavLink
                                                        key={section.id}
                                                        as={PrismicLink}
                                                        $isDirectLink
                                                        {...section.directLink}
                                                    >
                                                        {section.label}
                                                    </s.NavLink>
                                                )
                                            } else {
                                                return null
                                            }
                                        })}

                                        <s.NavLink as={LocalizedLink} to="/pricing">
                                            {content.pricing}

                                            {content.show_pricing_badge && (
                                                <s.MenuBadge aria-hidden="true" />
                                            )}
                                        </s.NavLink>
                                    </s.NavGroupLeft>
                                )}

                                {!hideRightNav && (
                                    <s.NavGroupRight>
                                        {!hasMounted ? null : isLoggedIn ? (
                                            <s.CtaWrapper $menuOpen={menuOpen}>
                                                <s.NavCta
                                                    as={ExternalLink}
                                                    to={process.env.GATSBY_PLEO_APP_URL}
                                                >
                                                    {content.app_link_text}
                                                </s.NavCta>
                                            </s.CtaWrapper>
                                        ) : (
                                            <>
                                                {showExploreProduct && (
                                                    <s.NavLink
                                                        as={LocalizedLink}
                                                        to="/explore-product"
                                                    >
                                                        <s.VisibleInSmAndXl>
                                                            {content.explore_product}
                                                        </s.VisibleInSmAndXl>
                                                        <s.VisibleInMdAndLg aria-hidden>
                                                            {content.explore}
                                                        </s.VisibleInMdAndLg>
                                                    </s.NavLink>
                                                )}
                                                {showLogIn && (
                                                    <s.NavLink
                                                        as={ExternalLink}
                                                        to={`${process.env.GATSBY_PLEO_APP_URL}/login?lang=${currentLocale.language}`}
                                                    >
                                                        {content.log_in}
                                                    </s.NavLink>
                                                )}

                                                {currentLocale.code === 'en-gb' && (
                                                    <s.NavLink
                                                        as={LocalizedLink}
                                                        to="/demo"
                                                        onClick={() =>
                                                            track(
                                                                TrackingEvent.HeaderBookDemoActioned,
                                                                {action: 'clicked'}
                                                            )
                                                        }
                                                    >
                                                        {content.book_a_demo}
                                                    </s.NavLink>
                                                )}

                                                <s.CtaWrapper $menuOpen={menuOpen}>
                                                    {getCtaButton(ctaDestination)}
                                                </s.CtaWrapper>
                                            </>
                                        )}
                                    </s.NavGroupRight>
                                )}
                            </s.Nav>

                            {(!hideLeftNav || isSmallScreen) && (
                                <s.MobileMenuBtn onClick={toggleMenu}>
                                    <img src={menuOpen ? menuCloseIcon : menuOpenIcon} alt={menu} />
                                </s.MobileMenuBtn>
                            )}
                        </>
                    )}
                </s.HeaderInner>
            </s.HeaderWrapper>

            <s.ScrollTrigger ref={mobileCtaRef} $threshold="330px" />

            {hasNonEmptyItems(content.signup_modal) && (
                <SignupModal
                    formId={getEnvironmentFormId(
                        content.signup_modal[0].hubspot_form_id_production,
                        content.signup_modal[0].hubspot_form_id_staging
                    )}
                    isOpen={modalOpen}
                    onDismiss={() => setModalOpen(false)}
                    image={content.signup_modal[0].image}
                    title={content.signup_modal[0].title}
                    subtitle={content.signup_modal[0].subtitle}
                    highlightedFeatures={highlightedFeatures}
                />
            )}
        </>
    )
}

const HeaderContainer = (props: Props) => {
    const data = useStaticQuery<HeaderQuery>(query)
    const [content] = useLocalizedContent(data.allPrismicHeader.nodes)

    return <Header content={content.data} {...props} />
}

const query = graphql`
    query Header {
        allPrismicHeader {
            nodes {
                lang
                data {
                    pricing
                    show_pricing_badge
                    partners
                    start_free_trial
                    book_a_demo
                    log_in
                    explore
                    explore_product
                    app_link_text
                    is_beta_label
                    product_button_label
                    product_dropdown_title {
                        richText
                    }
                    product_dropdown_body {
                        richText
                    }
                    product_dropdown_links {
                        link_title
                        link {
                            ...PrismicLink
                        }
                    }
                    product_dropdown_content {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            richText
                        }
                        icon_background_color
                        is_beta
                        link {
                            ...PrismicLink
                        }
                    }
                    why_pleo_button_label
                    why_pleo_dropdown_title {
                        richText
                    }
                    why_pleo_dropdown_body {
                        richText
                    }
                    why_pleo_dropdown_links {
                        link_title
                        link {
                            ...PrismicLink
                        }
                    }
                    why_pleo_dropdown_team_label
                    why_pleo_dropdown_team_short_sentence
                    why_pleo_dropdown_content_team {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        link {
                            ...PrismicLink
                        }
                    }
                    why_pleo_dropdown_industry_label
                    why_pleo_dropdown_industry_short_sentence
                    why_pleo_dropdown_content_industry {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        link {
                            ...PrismicLink
                        }
                    }
                    why_pleo_dropdown_company_size_label
                    why_pleo_dropdown_company_size_short_sentence
                    why_pleo_dropdown_content_company_size {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        link {
                            ...PrismicLink
                        }
                    }
                    resources_button_label
                    resources_dropdown_title {
                        richText
                    }
                    resources_dropdown_body {
                        richText
                    }
                    resources_dropdown_links {
                        link_title
                        link {
                            ...PrismicLink
                        }
                    }
                    resources_dropdown_content {
                        icon {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            richText
                        }
                        link {
                            ...PrismicLink
                        }
                    }
                    partners_button_label
                    partners_button_link {
                        ...PrismicLink
                    }
                    signup_modal {
                        title
                        subtitle
                        highlighted_feature_1
                        highlighted_feature_2
                        highlighted_feature_3
                        hubspot_form_id_staging
                        hubspot_form_id_production
                        image {
                            alt
                            url
                            copyright
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    dropdowns {
                        dropdown {
                            document {
                                ... on PrismicHeaderDropdown {
                                    data {
                                        columns {
                                            column {
                                                document {
                                                    ... on PrismicHeaderDropdownColumn {
                                                        id
                                                        data {
                                                            icon {
                                                                alt
                                                                url
                                                                copyright
                                                                dimensions {
                                                                    height
                                                                    width
                                                                }
                                                            }
                                                            label
                                                            links {
                                                                link_text
                                                                link {
                                                                    ...PrismicLink
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        cta_column {
                                            document {
                                                ... on PrismicHeaderDropdownCta {
                                                    data {
                                                        background_color
                                                        body
                                                        cta_text
                                                        cta_link {
                                                            ...PrismicLink
                                                        }
                                                        image {
                                                            alt
                                                            url
                                                            copyright
                                                            dimensions {
                                                                height
                                                                width
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default HeaderContainer
